/**
 * Dashboard — vue d'ensemble admin avec KPIs et graphiques Recharts.
 */
import { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import { Link } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import { dashboardApi, donsApi, quetesApi, evenementsApi, benevolesApi, inscriptionsApi } from "../services/api";
import { MOCK_DASHBOARD } from "../services/mockData";
import useAuth from "../hooks/useAuth";
import usePresenceAlerts from "../hooks/usePresenceAlerts";
import useInscriptionsPending from "../hooks/useInscriptionsPending";
import useToast from "../hooks/useToast";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Avatar from "../components/Avatar";

const PIE_COLORS = ["#E4A72C", "#F5C55A", "#60A5FA", "#2DD4A0", "#B8861F", "#E05B6A"];

/** Tooltip personnalisé pour s'intégrer au design dark. */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: colors.surface,
        border: `1px solid ${colors.gold}`,
        borderRadius: "8px",
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.6)",
      }}
    >
      <div style={{ color: colors.textMuted, fontSize: "12px", marginBottom: "4px" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontWeight: 600, fontSize: "14px" }}>
          {p.value.toLocaleString("fr-FR")}{p.unit || ""}
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { usingMock } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (usingMock) {
        setData(MOCK_DASHBOARD);
        setLoading(false);
        return;
      }
      try {
        const { data: d } = await dashboardApi.get();
        setData(d);
      } catch {
        setData(MOCK_DASHBOARD);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [usingMock]);

  if (loading) {
    return <div style={{ color: colors.textMuted, fontFamily: fonts.body }}>Chargement du tableau de bord...</div>;
  }

  if (!data) {
    return <div style={{ color: colors.danger }}>Erreur de chargement</div>;
  }

  const { kpi, dons_mensuels, repartition_dons, nouveaux_membres, repartition_membres } = data;

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      {/* Header */}
      <header style={{ marginBottom: "32px" }}>
        <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "6px" }}>
          Tableau de bord
        </h1>
        <p style={{ color: colors.textMuted, fontSize: "14px" }}>
          Vue d'ensemble de votre ministère
        </p>
      </header>

      {/* Widget validation des inscriptions (visible si fidèles en attente ou en-tête vide) */}
      <NouveauxInscritsWidget />

      {/* Widgets : Suivi pastoral + Finances + Prochain culte */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        <PastoralAlertsWidget inline />
        <FinanceMonthWidget />
        <NextCulteWidget />
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "32px" }}>
        <KpiCard label="Membres totaux"  value={kpi.total_membres}        icon="👥" trend={`+${kpi.nouveaux_membres_mois} ce mois`} />
        <KpiCard label="Membres actifs"  value={kpi.membres_actifs}       icon="✓"  variant="success" />
        <KpiCard label="Dons ce mois"    value={fmtMoney(kpi.total_dons_mois, kpi.devise)}    icon="💰" trend="+12% vs mois -1" />
        <KpiCard label="Dons cette année" value={fmtMoney(kpi.total_dons_annee, kpi.devise)} icon="📈" variant="gold" />
        <KpiCard label="Églises"         value={kpi.nb_eglises}           icon="⛪" />
        <KpiCard label="Événements à venir" value={kpi.nb_evenements_a_venir} icon="📅" />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px", marginBottom: "16px" }}>
        {/* Évolution des dons */}
        <Card>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "16px" }}>{dons_mensuels.title}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dons_mensuels.points}>
              <defs>
                <linearGradient id="grad-gold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E4A72C" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#E4A72C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
              <XAxis dataKey="label" stroke={colors.textMuted} fontSize={12} tickLine={false} />
              <YAxis stroke={colors.textMuted} fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="value" stroke={colors.gold} strokeWidth={2.5} fill="url(#grad-gold)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Répartition des dons (Pie) */}
        <Card>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "16px" }}>{repartition_dons.title}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={repartition_dons.points}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
              >
                {repartition_dons.points.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke={colors.bg} strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: "13px", color: colors.textMuted }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Charts row 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: "16px" }}>
        {/* Nouveaux membres (bar) */}
        <Card>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "16px" }}>{nouveaux_membres.title}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={nouveaux_membres.points}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
              <XAxis dataKey="label" stroke={colors.textMuted} fontSize={12} tickLine={false} />
              <YAxis stroke={colors.textMuted} fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={colors.gold} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Répartition des membres (line/bar simple) */}
        <Card>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", marginBottom: "16px" }}>{repartition_membres.title}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={repartition_membres.points} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border} horizontal={false} />
              <XAxis type="number" stroke={colors.textMuted} fontSize={12} tickLine={false} axisLine={false} />
              <YAxis dataKey="label" type="category" stroke={colors.textMuted} fontSize={13} tickLine={false} axisLine={false} width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={colors.goldLight} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

/** Carte KPI réutilisable. */
function KpiCard({ label, value, icon, trend, variant = "default" }) {
  const accent = variant === "gold" ? colors.gold : variant === "success" ? colors.success : colors.text;
  return (
    <Card hover>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "12px" }}>
        <span style={{ fontSize: "13px", color: colors.textMuted, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: "20px", opacity: 0.9 }}>{icon}</span>
      </div>
      <div style={{ fontFamily: fonts.heading, fontSize: "30px", fontWeight: 700, color: accent, lineHeight: 1.1 }}>
        {value}
      </div>
      {trend && (
        <div style={{ marginTop: "8px" }}>
          <Badge variant="success" size="sm" dot>{trend}</Badge>
        </div>
      )}
    </Card>
  );
}

/** Formate un montant avec sa devise. */
function fmtMoney(amount, devise = "EUR") {
  const symbols = { EUR: "€", USD: "$", XOF: "F CFA" };
  const sym = symbols[devise] || devise;
  return `${(amount || 0).toLocaleString("fr-FR")} ${sym}`;
}

/** Widget « Suivi pastoral » : aperçu des alertes de présence. */
function PastoralAlertsWidget({ inline = false }) {
  const { rouge, orange, jaune, total_alertes, loading } = usePresenceAlerts();

  if (loading) return null;
  if (total_alertes === 0) return null;

  const lines = [
    { label: "Absents + 1 mois",   count: rouge.length,  emoji: "🔴", color: colors.rose },
    { label: "Absents 3 semaines", count: orange.length, emoji: "🟠", color: "#FB923C" },
    { label: "Absents 2 semaines", count: jaune.length,  emoji: "🟡", color: colors.gold },
  ];

  return (
    <Card style={{
      marginBottom: inline ? 0 : "24px",
      background: rouge.length > 0
        ? `linear-gradient(135deg, rgba(224,91,106,0.10) 0%, ${colors.card} 60%)`
        : colors.card,
      border: `1px solid ${rouge.length > 0 ? "rgba(224,91,106,0.35)" : colors.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "22px" }}>⚠</span>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px", fontWeight: 600 }}>
            Suivi pastoral
          </h3>
        </div>
        <Link to="/presences" style={{ textDecoration: "none" }}>
          <Button size="sm" variant="ghost">Voir les alertes →</Button>
        </Link>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
        {lines.map((line) => (
          <div
            key={line.label}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              background: colors.bg,
              borderRadius: "10px",
              border: `1px solid ${colors.border}`,
              borderLeft: `3px solid ${line.color}`,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: colors.textMuted }}>
              <span style={{ fontSize: "16px" }}>{line.emoji}</span>
              {line.label}
            </span>
            <span style={{
              fontFamily: fonts.heading,
              fontSize: "26px",
              fontWeight: 700,
              color: line.count > 0 ? line.color : colors.textDim,
              lineHeight: 1,
            }}>
              {line.count}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Widget « Finances du mois » : breakdown dons / quêtes / organisations. */
function FinanceMonthWidget() {
  const [donsThisMonth, setDonsThisMonth] = useState([]);
  const [queteStats, setQueteStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const debut = `${yyyy}-${mm}-01`;

    Promise.all([
      donsApi.list({ date_min: debut }).then((r) => r.data.items || []).catch(() => []),
      quetesApi.stats().then((r) => r.data).catch(() => null),
    ])
      .then(([dons, stats]) => {
        setDonsThisMonth(dons);
        setQueteStats(stats);
      })
      .finally(() => setLoading(false));
  }, []);

  const moisLabel = new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  // Aggregations en valeur "pseudo-EUR" pour affichage simple
  const totals = useMemo(() => {
    let dons_membres = 0, dons_anonymes = 0, dons_orgs = 0;
    donsThisMonth.forEach((d) => {
      const m = d.montant || 0;
      if (d.type_donateur === "anonyme")           dons_anonymes += m;
      else if (d.type_donateur === "organisation") dons_orgs += m;
      else                                          dons_membres += m;
    });
    return { dons_membres, dons_anonymes, dons_orgs };
  }, [donsThisMonth]);

  const queteMois = queteStats?.total_mois || 0;
  const moyenneCulte = queteStats?.moyenne_par_culte || 0;
  const meilleureQuete = queteStats?.meilleure_quete || 0;
  const nbQuetes = queteStats?.nb_quetes_mois || 0;
  const totalGlobal = totals.dons_membres + totals.dons_anonymes + totals.dons_orgs + queteMois;

  const fmtAmount = (v) => `${(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}`;

  if (loading) return null;
  if (totalGlobal === 0) return null;

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "22px" }}>💰</span>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px", fontWeight: 600, textTransform: "capitalize" }}>
            Finances — {moisLabel}
          </h3>
        </div>
        <Link to="/finances" style={{ textDecoration: "none" }}>
          <Button size="sm" variant="ghost">Voir le détail →</Button>
        </Link>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <FinanceLine label="👤 Dons individuels" amount={totals.dons_membres} />
        {nbQuetes > 0 && (
          <FinanceLine label={`⛪ Quêtes (${nbQuetes} culte${nbQuetes > 1 ? "s" : ""})`} amount={queteMois} />
        )}
        {totals.dons_orgs > 0 && (
          <FinanceLine label="🏛 Organisations" amount={totals.dons_orgs} />
        )}
        {totals.dons_anonymes > 0 && (
          <FinanceLine label="🎭 Dons anonymes" amount={totals.dons_anonymes} />
        )}

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          paddingTop: "10px", marginTop: "4px",
          borderTop: `1px solid ${colors.border}`,
        }}>
          <span style={{ fontSize: "12px", letterSpacing: "0.05em", textTransform: "uppercase", color: colors.textMuted, fontWeight: 600 }}>
            Total du mois
          </span>
          <span style={{ fontFamily: fonts.heading, fontSize: "26px", fontWeight: 700, color: colors.gold }}>
            {fmtAmount(totalGlobal)}
          </span>
        </div>
      </div>

      {(moyenneCulte > 0 || meilleureQuete > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${colors.border}` }}>
          <div>
            <div style={{ fontSize: "11px", color: colors.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Moyenne quête / culte
            </div>
            <div style={{ fontFamily: fonts.heading, fontSize: "18px", fontWeight: 700, color: colors.text, marginTop: "2px" }}>
              {fmtAmount(moyenneCulte)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "11px", color: colors.textMuted, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Meilleure quête
            </div>
            <div style={{ fontFamily: fonts.heading, fontSize: "18px", fontWeight: 700, color: colors.gold, marginTop: "2px" }}>
              {fmtAmount(meilleureQuete)}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Widget "Prochain culte" avec statut bénévoles. */
function NextCulteWidget() {
  const [evt, setEvt] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    evenementsApi.list({ type: "culte" })
      .then(({ data }) => {
        const now = Date.now();
        const upcoming = (data || [])
          .filter((e) => new Date(e.date_debut).getTime() >= now)
          .sort((a, b) => new Date(a.date_debut) - new Date(b.date_debut));
        const next = upcoming[0] || (data || [])[0];   // fallback : dernier passé si rien à venir
        setEvt(next);
        if (next) {
          benevolesApi.summaryEvenement(next.id)
            .then(({ data: s }) => setSummary(s))
            .catch(() => setSummary(null));
        }
      })
      .catch(() => setEvt(null));
  }, []);

  if (!evt) return null;

  const dateStr = new Date(evt.date_debut).toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
  });
  const heureStr = new Date(evt.date_debut).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  const total = summary?.total || 0;
  const confirmes = summary?.confirmes || 0;
  const pct = total > 0 ? (confirmes / total) * 100 : 0;
  const couleur = pct === 100 ? "#2DD4A0" : pct >= 50 ? colors.gold : "#FB923C";

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", gap: "10px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "22px" }}>⛪</span>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px", fontWeight: 600 }}>
            Prochain culte
          </h3>
        </div>
        <Link to="/benevoles" style={{ textDecoration: "none" }}>
          <Button size="sm" variant="ghost">Bénévoles →</Button>
        </Link>
      </div>

      <div style={{ marginBottom: "14px" }}>
        <div style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600, textTransform: "capitalize" }}>
          {evt.titre}
        </div>
        <div style={{ color: colors.textMuted, fontSize: "13px", marginTop: "4px", textTransform: "capitalize" }}>
          {dateStr} · {heureStr}
        </div>
      </div>

      {summary && total > 0 ? (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "6px" }}>
            <span style={{ color: colors.textMuted }}>Bénévoles confirmés</span>
            <span style={{ color: couleur, fontWeight: 600 }}>
              {confirmes}/{total}
            </span>
          </div>
          <div style={{ height: 8, background: colors.border, borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              width: `${pct}%`,
              height: "100%",
              background: couleur,
              transition: "width 400ms",
            }} />
          </div>
          {summary.en_attente > 0 && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#FB923C" }}>
              ⏳ {summary.en_attente} en attente de réponse
            </div>
          )}
          {summary.refuses > 0 && (
            <div style={{ marginTop: "4px", fontSize: "12px", color: colors.rose }}>
              ✗ {summary.refuses} à remplacer
            </div>
          )}
        </div>
      ) : (
        <div style={{
          padding: "10px 12px",
          background: "rgba(251,146,60,0.08)",
          border: "1px solid rgba(251,146,60,0.3)",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#FB923C",
        }}>
          ⚠ Aucun bénévole assigné — pensez à organiser le service.
        </div>
      )}
    </Card>
  );
}

function FinanceLine({ label, amount }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "4px 0" }}>
      <span style={{ color: colors.text, fontSize: "14px" }}>{label}</span>
      <span style={{ color: colors.text, fontSize: "14px", fontVariantNumeric: "tabular-nums" }}>
        {(amount || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
      </span>
    </div>
  );
}

/** Widget : fidèles en attente de validation. */
function NouveauxInscritsWidget() {
  const { items, count, loading, refresh } = useInscriptionsPending();
  const toast = useToast();
  const [busyId, setBusyId] = useState(null);

  // N'affiche le widget que si chargement terminé pour éviter le flash vide
  if (loading) return null;

  const handleValider = async (item) => {
    setBusyId(item.id);
    try {
      await inscriptionsApi.valider(item.id);
      toast.success(`✓ ${item.full_name} accepté — visible dans l'annuaire`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  const handleRefuser = async (item) => {
    if (!window.confirm(`Refuser ${item.full_name} ?\n\nSon compte sera désactivé.`)) return;
    setBusyId(item.id);
    try {
      await inscriptionsApi.refuser(item.id);
      toast.success("Inscription refusée");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card style={{
      marginBottom: 24,
      border: count > 0 ? `1px solid ${colors.goldBorder}` : `1px solid ${colors.border}`,
      background: count > 0 ? `linear-gradient(135deg, ${colors.goldDim} 0%, ${colors.card} 100%)` : colors.card,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: count === 0 ? 0 : 16 }}>
        <h2 style={{ fontFamily: fonts.heading, fontSize: 22, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
          🆕 Nouveaux inscrits
          {count > 0 && (
            <span style={{
              background: colors.rose,
              color: "#fff",
              padding: "2px 10px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 700,
            }}>
              {count}
            </span>
          )}
        </h2>
        {count > 0 && (
          <span style={{ color: colors.textMuted, fontSize: 13 }}>En attente de validation</span>
        )}
      </div>

      {count === 0 ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", color: colors.textMuted, fontSize: 14 }}>
          <span style={{ color: colors.green, fontSize: 18 }}>✓</span>
          Aucun inscrit en attente — tous les membres sont validés.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.slice(0, 5).map((item) => (
              <div key={item.id} style={{
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                padding: 12, background: colors.bg,
                borderRadius: 10, border: `1px solid ${colors.border}`,
              }}>
                <Avatar name={item.full_name} size="md" />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, color: colors.text }}>{item.full_name}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{item.email}</div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                    Inscrit {relativeTime(item.created_at)}
                    {item.eglise_nom && ` · ${item.eglise_nom}`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Button size="sm" onClick={() => handleValider(item)} disabled={busyId === item.id}>
                    ✓ Accepter
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleRefuser(item)} disabled={busyId === item.id}>
                    ✗ Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {items.length > 5 && (
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <Link to="/annuaire?tab=en-attente" style={{ color: colors.gold, fontSize: 13, fontWeight: 600 }}>
                Voir les {items.length} inscrits en attente →
              </Link>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function relativeTime(iso) {
  if (!iso) return "récemment";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "il y a quelques secondes";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}
